import type { FindingKind } from "@patent-studio/api-types";
import { HoverTip } from "@/components/ui/HoverTip";
import { StatusPill } from "@/components/ui/StatusPill";

/**
 * The findings the server raised on an element's rungs, each as the ids it
 * holds for, so the page draws them and never reads the rule itself.
 */
export interface RungFindings {
  /** The rungs resting only on passages the inventor has not decided. */
  uncertainOnly: readonly string[];
  /** The values whose ranges are equal to another value's, or overlap it in part. */
  overlapping: readonly string[];
}

const UNCERTAIN_SOURCES_ONLY: FindingKind = "uncertain_sources_only";

/**
 * Beside where an element or a rung came from, where every passage it rests on
 * is one the inventor has not decided: an open question, a limitation or an
 * alternative. The server names what the finding holds for, so the page draws
 * the rule and never restates it. Where the model was asked to fix it and kept
 * it instead, the reason it gave is part of the finding.
 */
export function OpenQuestionOnly({
  id,
  uncertainOnly,
  keptReasons,
  className,
}: {
  id: string;
  uncertainOnly: readonly string[];
  keptReasons?: Readonly<Record<string, string>>;
  className?: string;
}) {
  if (!uncertainOnly.includes(id)) return null;
  const kept = keptReasons?.[UNCERTAIN_SOURCES_ONLY];
  return (
    <HoverTip
      tone="dark"
      tip={
        <span>
          Every passage this rests on is an open question, a limitation or an
          alternative the inventor has not decided.
          {kept !== undefined && <> Kept by the model: {kept}</>}
        </span>
      }
    >
      {({ triggerProps }) => (
        <StatusPill
          {...triggerProps}
          tabIndex={0}
          tone="warning"
          size="sm"
          plain
          data-testid={`uncertain-${id}`}
          className={className}
        >
          Open question only
        </StatusPill>
      )}
    </HoverTip>
  );
}

/**
 * Beside a value whose range is equal to another value's of its element, or
 * overlaps it in part. The server names the values at fault, so the page draws
 * the finding and never reads the ranges itself.
 */
export function Overlaps({
  id,
  overlapping,
}: {
  id: string;
  overlapping: readonly string[];
}) {
  if (!overlapping.includes(id)) return null;
  return (
    <HoverTip
      tone="dark"
      tip={
        <span>
          This range is equal to another value&apos;s range of this element, or
          overlaps it in part.
        </span>
      }
    >
      {({ triggerProps }) => (
        <StatusPill
          {...triggerProps}
          tabIndex={0}
          tone="warning"
          size="sm"
          plain
          data-testid={`overlap-${id}`}
          className="ml-2"
        >
          Overlaps another value
        </StatusPill>
      )}
    </HoverTip>
  );
}
